import { Component } from '@theme/component';

/**
 * "Product tabs" section: collection tabs (optionally split in two groups by a pill switch)
 * that show/hide product panels. Follows the WAI-ARIA tabs pattern.
 *
 * Only the first tab's grid is rendered with the page; the other grids are fetched on demand
 * (`data-src`, the `collection.product-tabs` template) and swapped in, keeping the page light.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} [tablist]
 * @property {HTMLButtonElement[]} [switchButtons]
 * @property {HTMLElement} [switchIndicator]
 *
 * @extends Component<Refs>
 */
class ProductTabs extends Component {
  requiredRefs = ['tabs', 'panels'];

  #resizeObserver = new ResizeObserver(() => this.#moveSwitchIndicator());
  /** @type {Map<HTMLElement, Promise<void>>} */
  #pending = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.#moveSwitchIndicator();
    document.fonts?.ready.then(() => this.#moveSwitchIndicator());

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  get #group() {
    const selected = this.refs.switchButtons?.find((button) => button.getAttribute('aria-selected') === 'true');
    return selected?.dataset.group ?? this.dataset.initialGroup ?? 'a';
  }

  get #selectedIndex() {
    return Math.max(
      0,
      this.refs.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
  }

  /** Tabs that belong to the active group, in DOM order. */
  get #visibleTabs() {
    return this.refs.tabs.filter((tab) => !tab.hidden);
  }

  /**
   * Switches between the two tab groups (e.g. Women / Men) and selects the group's first tab.
   * @param {string} group
   */
  selectGroup(group) {
    const { switchButtons = [], tabs } = this.refs;

    switchButtons.forEach((button) => {
      button.setAttribute('aria-selected', String(button.dataset.group === group));
    });

    let first = -1;
    tabs.forEach((tab, index) => {
      const visible = tab.dataset.group === group;
      tab.hidden = !visible;
      if (visible && first === -1) first = index;
    });

    if (first !== -1) this.selectTab(first);
    this.#moveSwitchIndicator();
  }

  /**
   * Selects a tab (and its panel) by index.
   * @param {number | string} index
   * @param {Event} [event]
   */
  selectTab(index, event) {
    const selected = Number(index);
    const { tabs, panels } = this.refs;

    tabs.forEach((tab, i) => {
      const active = i === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== selected;
    });

    const tab = tabs[selected];
    if (!tab) return;

    const panel = panels[selected];
    if (panel) this.#loadPanel(panel, tab.dataset.badge);

    if (event instanceof KeyboardEvent) tab.focus();

    // Keep the active chip in view on the horizontally scrolling mobile tab bar.
    if (event && this.refs.tablist && this.refs.tablist.scrollWidth > this.refs.tablist.clientWidth) {
      tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }
  }

  /**
   * Fetches the first tab of a group ahead of time (pointer over / focus on the switch).
   * @param {string} group
   */
  prefetchGroup(group) {
    const index = this.refs.tabs.findIndex((tab) => tab.dataset.group === group);
    if (index !== -1) this.prefetchTab(index);
  }

  /**
   * Fetches a tab's grid ahead of time (pointer over / focus on its chip).
   * @param {number | string} index
   */
  prefetchTab(index) {
    const panel = this.refs.panels[Number(index)];
    const tab = this.refs.tabs[Number(index)];
    if (panel && tab) this.#loadPanel(panel, tab.dataset.badge);
  }

  /**
   * Replaces a panel's pending grid with the grid rendered by the `collection.product-tabs` template.
   * @param {HTMLElement} panel
   * @param {string} [badge] - Label shown on the first product (from the tab block)
   */
  #loadPanel(panel, badge) {
    const pending = panel.querySelector('.product-tabs__grid--pending');
    if (!(pending instanceof HTMLElement) || !pending.dataset.src) return;

    const inFlight = this.#pending.get(panel);
    if (inFlight) return;

    const request = fetch(pending.dataset.src, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Product tabs: ${response.status} for ${pending.dataset.src}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('[data-product-tabs-panel] .product-tabs__grid');
        if (!grid) throw new Error('Product tabs: no grid in the fetched panel');

        // Keep the column count and gaps of the grid rendered with the page.
        const reference = this.querySelector('.product-tabs__grid .resource-list');
        const fetched = grid.querySelector('.resource-list');
        if (reference && fetched) fetched.setAttribute('style', reference.getAttribute('style') ?? '');

        if (badge) this.#addBadge(grid, badge);
        pending.replaceWith(document.adoptNode(grid));
      })
      .catch((error) => {
        console.warn(error);
        pending.removeAttribute('aria-busy');
        this.#pending.delete(panel);
      });

    this.#pending.set(panel, request);
  }

  /**
   * Marks the first product of a fetched grid with the tab's badge (e.g. "Nº1").
   * @param {Element} grid
   * @param {string} badge
   */
  #addBadge(grid, badge) {
    const item = grid.querySelector('.product-tabs__item');
    if (!item) return;

    const label = document.createElement('span');
    label.className = 'product-tabs__badge';
    label.textContent = badge;
    item.classList.add('product-tabs__item--badged');
    item.prepend(label);
  }

  /**
   * Arrow-key navigation between the visible tabs (WAI-ARIA tabs pattern).
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const visible = this.#visibleTabs;
    const current = Math.max(0, visible.indexOf(this.refs.tabs[this.#selectedIndex]));
    const count = visible.length;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % count;
        break;
      case 'ArrowLeft':
        next = (current - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const tab = visible[next];
    if (tab) this.selectTab(this.refs.tabs.indexOf(tab), event);
  }

  /**
   * Theme editor: selecting a tab block shows that tab.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.dataset.blockId) return;

    const index = this.refs.tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (index === -1) return;

    const group = target.dataset.group;
    if (group && group !== this.#group) this.selectGroup(group);
    this.selectTab(index);
  };

  #moveSwitchIndicator() {
    const { switchIndicator, switchButtons = [] } = this.refs;
    const active = switchButtons.find((button) => button.getAttribute('aria-selected') === 'true');

    if (!switchIndicator || !active) return;

    switchIndicator.style.setProperty('--indicator-left', `${active.offsetLeft}px`);
    switchIndicator.style.setProperty('--indicator-width', `${active.offsetWidth}px`);
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
